import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    collection,
    addDoc,
    getDocs,
    updateDoc,
    doc,
    query,
    where,
    Timestamp
} from "firebase/firestore";
import { db } from "../../firebase/config";

const LEAVES_COLLECTION = "leaves";

export const fetchUserLeaves = createAsyncThunk(
    "leaveManagement/fetchUserLeaves",
    async (empID, { rejectWithValue }) => {
        try {
            const leavesRef = collection(db, LEAVES_COLLECTION);
            const q = query(leavesRef, where("empID", "==", empID));

            const snapshot = await getDocs(q);

            const leaves = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    // Convert Firestore timestamps to regular dates for easier handling
                    startDate: data.startDate?.toDate
                        ? data.startDate.toDate()
                        : data.startDate,
                    endDate: data.endDate?.toDate
                        ? data.endDate.toDate()
                        : data.endDate,
                    leaveType: data.leaveType || "Unknown",
                    leaveDays: data.leaveDays || 0,
                    leaveRemarks: data.leaveRemarks || null,
                    status: data.status || "Pending",
                    // Handle different field names for compatibility
                    empID: data.empID,
                    empEmail: data.empEmail,
                };
            });

            console.log("Fetched leaves:", leaves);
            return leaves;
        } catch (error) {
            console.error("Error fetching leaves:", error);
            return rejectWithValue(
                error.message || "Failed to fetch leave requests"
            );
        }
    }
);

export const addLeaveRequest = createAsyncThunk(
    "leaveManagement/addLeaveRequest",
    async (leaveData, { rejectWithValue }) => {
        try {
            console.log('addLeaveRequest called with:', leaveData);
            
            // Add leave document to Firestore
            const leaveWithDates = {
                empID: leaveData.empID,
                empEmail: leaveData.empEmail || leaveData.employeeName,
                startDate: Timestamp.fromDate(leaveData.startDate),
                endDate: Timestamp.fromDate(leaveData.endDate),
                leaveDays: leaveData.leaveDays,
                leaveType: leaveData.leaveType,
                leaveRemarks: leaveData.leaveRemarks || null,
                status: "Pending",
                preLeaveBalance: 30, // Set default values
                preLeaveTaken: 0,
            };

            const docRef = await addDoc(
                collection(db, LEAVES_COLLECTION),
                leaveWithDates
            );

            console.log('Leave request successfully added to Firebase with ID:', docRef.id);

            return {
                id: docRef.id,
                ...leaveWithDates,
                startDate: leaveData.startDate,
                endDate: leaveData.endDate,
            };
        } catch (error) {
            console.error("Error adding leave request:", error);
            return rejectWithValue(
                error.message || "Failed to add leave request"
            );
        }
    }
);

export const updateLeaveRemarks = createAsyncThunk(
    "leaveManagement/updateLeaveRemarks",
    async ({ leaveId, remarks }, { rejectWithValue }) => {
        try {
            const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);

            await updateDoc(leaveRef, {
                leaveRemarks: remarks,
            });

            return {
                leaveId,
                leaveRemarks: remarks,
            };
        } catch (error) {
            console.error("Error updating leave remarks:", error);
            return rejectWithValue(
                error.message || "Failed to update leave remarks"
            );
        }
    }
);

export const cancelLeaveRequest = createAsyncThunk(
    "leaveManagement/cancelLeaveRequest",
    async (leaveId, { rejectWithValue }) => {
        try {
            const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);

            await updateDoc(leaveRef, {
                status: "Cancelled",
            });

            return {
                leaveId,
                status: "Cancelled",
            };
        } catch (error) {
            console.error("Error cancelling leave request:", error);
            return rejectWithValue(
                error.message || "Failed to cancel leave request"
            );
        }
    }
);

const initialState = {
    leaves: [],
    loading: false,
    error: null,
    addingLeave: false,
    addLeaveError: null,
    updatingRemarks: false,
    updateRemarksError: null,
    cancellingLeave: false,
    cancelLeaveError: null,
    userLeaveBalance: {
        casual: 10,
        sick: 7,
        annual: 15,
        unpaid: 0,
    },
};

const leaveManagementSlice = createSlice({
    name: "leaveManagement",
    initialState,
    reducers: {
        clearErrors: (state) => {
            state.error = null;
            state.addLeaveError = null;
            state.updateRemarksError = null;
            state.cancelLeaveError = null;
        },
        setUserLeaveBalance: (state, action) => {
            state.userLeaveBalance = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchUserLeaves
            .addCase(fetchUserLeaves.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserLeaves.fulfilled, (state, action) => {
                state.leaves = action.payload;
                state.loading = false;
            })
            .addCase(fetchUserLeaves.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload || "Failed to fetch leave requests";
            })

            // addLeaveRequest
            .addCase(addLeaveRequest.pending, (state) => {
                state.addingLeave = true;
                state.addLeaveError = null;
            })
            .addCase(addLeaveRequest.fulfilled, (state, action) => {
                state.leaves = [action.payload, ...state.leaves];
                state.addingLeave = false;
            })
            .addCase(addLeaveRequest.rejected, (state, action) => {
                state.addingLeave = false;
                state.addLeaveError =
                    action.payload || "Failed to add leave request";
            })

            // updateLeaveRemarks
            .addCase(updateLeaveRemarks.pending, (state) => {
                state.updatingRemarks = true;
                state.updateRemarksError = null;
            })
            .addCase(updateLeaveRemarks.fulfilled, (state, action) => {
                const { leaveId, leaveRemarks } = action.payload;

                state.leaves = state.leaves.map((leave) =>
                    leave.id === leaveId ? { ...leave, leaveRemarks } : leave
                );

                state.updatingRemarks = false;
            })
            .addCase(updateLeaveRemarks.rejected, (state, action) => {
                state.updatingRemarks = false;
                state.updateRemarksError =
                    action.payload || "Failed to update leave remarks";
            })

            // cancelLeaveRequest
            .addCase(cancelLeaveRequest.pending, (state) => {
                state.cancellingLeave = true;
                state.cancelLeaveError = null;
            })
            .addCase(cancelLeaveRequest.fulfilled, (state, action) => {
                const { leaveId, status } = action.payload;

                state.leaves = state.leaves.map((leave) =>
                    leave.id === leaveId ? { ...leave, status } : leave
                );

                state.cancellingLeave = false;
            })
            .addCase(cancelLeaveRequest.rejected, (state, action) => {
                state.cancellingLeave = false;
                state.cancelLeaveError =
                    action.payload || "Failed to cancel leave request";
            });
    },
});

export const { clearErrors, setUserLeaveBalance } =
    leaveManagementSlice.actions;

export default leaveManagementSlice.reducer;
